/**
 * Where the application is running, and whether that is good enough to trust
 * it with unsent work — ADR-0004 §2.8 and §2.9.
 *
 * The one fact that drives all of this: iOS clears the storage of a page left
 * in a Safari tab after about seven days of inactivity, and gives no such
 * treatment to an app started from the home screen. A site manager off sick
 * for a week must not come back to an empty queue, and would have no way of
 * knowing entries had gone — so on iOS, offline entry is not offered at all
 * until the app is installed. Blunt, and the alternative is silent data loss
 * discovered at payroll.
 *
 * Android keeps the storage either way, so nothing is withheld there; the
 * install prompt is an improvement, not a precondition.
 */

/** True when the app was started from the home screen rather than a tab. */
export function jeInstalirano(): boolean {
  // `standalone` on the navigator is the iOS-only signal and is not in the
  // DOM types; `display-mode` is the standard one everybody else answers.
  const iosStandalone = (navigator as Navigator & { standalone?: boolean })
    .standalone;
  if (iosStandalone === true) {
    return true;
  }
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches
  );
}

/**
 * True on iPhone and iPad.
 *
 * iPadOS reports itself as a Mac, and the touch points are what give it away —
 * a real Mac reports none. Sniffing the platform is unpleasant, but the
 * seven-day storage rule is a platform fact and there is nothing to feature
 * detect: the storage disappears later, not now.
 */
export function jeIOS(): boolean {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) {
    return true;
  }
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

/**
 * Whether entries may be queued on this device.
 *
 * The single question the entry screen asks. Everywhere except an uninstalled
 * iOS the answer is yes.
 */
export function offlineUnosDostupan(): boolean {
  return !jeIOS() || jeInstalirano();
}

/**
 * The event Chrome fires when the app could be installed.
 *
 * Not in the DOM types, because it is not in the standard: Android has it,
 * iOS does not and will not, which is why §2.9 exists at all. Captured rather
 * than left alone so the offer is made in our own words at a moment that makes
 * sense — after signing in, by somebody who is about to enter hours — instead
 * of by the browser's own strip at whatever moment it chooses.
 */
export interface DogadajPonude extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/**
 * That the offer was turned down, kept across restarts.
 *
 * In `localStorage` and not in the queue's database: this is a preference
 * about a banner, and it must not share a store with work that exists nowhere
 * else. Losing it costs one more banner.
 */
const KLJUC_ODBIJENE_PONUDE = "avm-instalacija-ponuda-odbijena";

export function ponudaOdbijena(): boolean {
  try {
    return localStorage.getItem(KLJUC_ODBIJENE_PONUDE) === "1";
  } catch {
    // Private mode on older iOS throws on access. A browser that will not
    // remember the refusal still gets the app.
    return false;
  }
}

export function zapamtiOdbijanje(): void {
  try {
    localStorage.setItem(KLJUC_ODBIJENE_PONUDE, "1");
  } catch {
    // As above: not remembering is a nuisance, not a failure.
  }
}

/**
 * Whether it is worth listening for the offer at all.
 *
 * iOS is excluded deliberately and not as an oversight: it has no such event,
 * and its route is the written steps in `UputaZaInstalaciju` (§2.9).
 */
export function ponudaMoguca(): boolean {
  return !jeIOS() && !jeInstalirano() && !ponudaOdbijena();
}

/**
 * Ask the browser to keep our storage when it is short of space.
 *
 * Called on every start rather than once (§2.8): iOS resets the grant, and a
 * permission asked for only at install time is a permission that quietly
 * lapses. A refusal is not an error — the queue still works, it is just
 * evictable — so this reports the answer and never throws.
 */
export async function zatraziTrajnuPohranu(): Promise<boolean> {
  if (!navigator.storage?.persist) {
    return false;
  }
  try {
    if (await navigator.storage.persisted()) {
      return true;
    }
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
